import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getPostfixWithRobotId, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { changeDataElemId } from "./sesItemConfigSlice";
import { ENDPOINTS_SES, URL_SES } from "@/constants/api_endpoints";
import { IAddDataElemProps, IAddingDataElemResponse, IDataElemItem, IDeleteDataElemProps, IEditDataElemProps, ISesDataElemState } from "@/types/ses/sesDataElemTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: ISesDataElemState = {
	dataList: {
		data: [],
		dictionary: {},
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		activeElementId: "",
		backgroundLoading: false,
	},
	addingData: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	editingData: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingData: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// список ЭД
export const getDataList = createAsyncThunk("sesDataElem/list", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IDataElemItem[] | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.DATA_LIST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление ЭД
export const addDataElem = createAsyncThunk("sesDataElem/add", async ({ robotId, data }: IAddDataElemProps, { dispatch, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddingDataElemResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.DATA_ADD}/${robotId}`, {
			data: JSON.stringify(data),
		});
		if (response.data.id) {
			dispatch(changeDataElemId(response.data.id));
		}
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение ЭД
export const editDataElem = createAsyncThunk("sesDataElem/edit", async ({ robotId, dataId, data }: IEditDataElemProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.DATA_EDIT}/${robotId}/${dataId}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление ЭД
export const deleteDataElem = createAsyncThunk("sesDataElem/delete", async ({ robotId, dataId }: IDeleteDataElemProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_SES}/${ENDPOINTS_SES.DATA_DELETE}/${robotId}/${dataId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesDataElemSlice = createSlice({
	name: "sesDataElem",
	initialState,
	reducers: {
		// добавление активного ЭД
		addActiveDataElem: (state, action: PayloadAction<string>) => {
			state.dataList.activeElementId = action.payload;
		},
		// изменение флага фоновой загрузки списка ЭД
		changeBackgroundDownloadDataElemList: (state, action: PayloadAction<boolean>) => {
			state.dataList.backgroundLoading = action.payload;
		},
		// -------------------------------------------------------------------------
		// очистка state
		clearState: (state) => {
			state.dataList = initialState.dataList;
			state.addingData = initialState.addingData;
			state.editingData = initialState.editingData;
			state.deletingData = initialState.deletingData;
		},
		// очистка списка ЭД
		clearDataList: (state) => {
			state.dataList = initialState.dataList;
		},
		// очистка статуса добавления ЭД
		clearAddingData: (state) => {
			state.addingData = initialState.addingData;
		},
		// очистка статуса изменения ЭД
		clearEditingData: (state) => {
			state.editingData = initialState.editingData;
		},
		// очистка статуса удаления ЭД
		clearDeletingData: (state) => {
			state.deletingData = initialState.deletingData;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getDataList.pending, (state) => {
				state.dataList.status = RequestStatus.LOADING;
			})
			.addCase(getDataList.fulfilled, (state, action) => {
				state.dataList.backgroundLoading = false; // сброс флага фоновой загрузки
				if (Array.isArray(action.payload)) {
					state.dataList.status = RequestStatus.IDLE;
					state.dataList.data = action.payload.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
					const dictionary: Record<string, [string, number]> = {}; // создаем словарь id- [name,weight]
					action.payload.forEach((dataElem) => (dictionary[dataElem.id] = [dataElem.name, dataElem.weight]));
					state.dataList.dictionary = dictionary;
				} else state.dataList.status = RequestStatus.FAILED;
			})
			.addCase(getDataList.rejected, (state, action: PayloadAction<unknown>) => {
				state.dataList.backgroundLoading = false; // сброс флага фоновой загрузки
				state.dataList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.dataList.error = error;
				state.dataList.message = message;
			})
			.addCase(addDataElem.pending, (state) => {
				state.addingData.status = RequestStatus.LOADING;
			})
			.addCase(addDataElem.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingData.status = RequestStatus.IDLE;
					state.addingData.error = action.payload.error;
					state.addingData.message = action.payload.message;
				} else state.addingData.status = RequestStatus.FAILED;
			})
			.addCase(addDataElem.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingData.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingData.error = error;
				state.addingData.message = message;
			})
			.addCase(editDataElem.pending, (state) => {
				state.editingData.status = RequestStatus.LOADING;
			})
			.addCase(editDataElem.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingData.status = RequestStatus.IDLE;
					state.editingData.error = action.payload.error;
					state.editingData.message = action.payload.message;
				} else state.editingData.status = RequestStatus.FAILED;
			})
			.addCase(editDataElem.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingData.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingData.error = error;
				state.editingData.message = message;
			})
			.addCase(deleteDataElem.pending, (state) => {
				state.deletingData.status = RequestStatus.LOADING;
			})
			.addCase(deleteDataElem.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingData.status = RequestStatus.IDLE;
					state.deletingData.error = action.payload.error;
					state.deletingData.message = action.payload.message;
				} else state.deletingData.status = RequestStatus.FAILED;
			})
			.addCase(deleteDataElem.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingData.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingData.error = error;
				state.deletingData.message = message;
			});
	},
});

export const { addActiveDataElem, changeBackgroundDownloadDataElemList, clearState, clearDataList, clearAddingData, clearEditingData, clearDeletingData } =
	sesDataElemSlice.actions;

export const selectDataList = (state: RootState) => state.sesDataElem.dataList;
export const selectActiveElementId = (state: RootState) => state.sesDataElem.dataList.activeElementId;
export const selectAddingData = (state: RootState) => state.sesDataElem.addingData;
export const selectEditingData = (state: RootState) => state.sesDataElem.editingData;
export const selectDeletingData = (state: RootState) => state.sesDataElem.deletingData;

export default sesDataElemSlice.reducer;
